import {useTranslation} from '@app/hooks/use-translation';
import {Box, Link, Tab, TabList, Tabs, tempo} from '@tempo/core';
import type {FC} from 'react';
import type {TAB_INDEX} from '@app/utils/deviceConfig';
import {tabOptions} from '@app/utils/deviceConfig';
import {usePathname} from 'next/navigation';
import NextLink from 'next/link';

const getActiveTab = (asPath: string): TAB_INDEX => {
	const asPathSegments = asPath.split('/').filter(Boolean);
	const lastSegment = asPathSegments[asPathSegments.length - 1];

	for (const tabOption of tabOptions) {
		if (lastSegment === tabOption.tab) {
			return tabOption.id;
		}

		// check on contentTabs then return the id
		if (tabOption.contentTabs.includes(lastSegment)) {
			return tabOption.id;
		}
	}

	return 0;
};

export const DeviceTabs: FC = () => {
	const {t} = useTranslation({ns: ['devices']});

	const pathname = usePathname();
	const activeTabId = getActiveTab(pathname ?? '/');

	return (
		<Tabs
			index={activeTabId}
			position="relative"
			variant="unstyled"
			maxW="100%"
		>
			<Box
				overflowY="hidden"
				sx={{
					scrollbarWidth: 'none',
					'::-webkit-scrollbar': {
						display: 'none',
					},
				}}
			>
				<TabList justifyContent={{base: 'left', md: 'center'}}>
					{tabOptions.map(({name, path, id}) => {
						const isTabActive = activeTabId === id;
						return (
							<Tab key={`${name}-${id}`} flexShrink={0} position="relative">
								<Link
									as={NextLink}
									_hover={{color: 'text.accent.primary.hovered'}}
									variant={isTabActive ? 'deezer' : 'default'}
									href={`/${path}`}
									alignItems="center"
								>
									{t(name)}
								</Link>
								{isTabActive && (
									<tempo.span
										height="2px"
										borderRadius="5px"
										position="absolute"
										left="0"
										bottom="0"
										w="100%"
										bg="border.neutral.primary.focused"
									/>
								)}
							</Tab>
						);
					})}
				</TabList>
			</Box>
		</Tabs>
	);
};
